if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.content = this.querySelector('.product-media-modal__content');
        this.prevButton = this.querySelector('[data-modal-prev]');
        this.nextButton = this.querySelector('[data-modal-next]');
        this.thumbs = this.querySelector('[data-modal-thumbs]');
        this.counter = this.querySelector('[data-modal-counter]');
        this.counterCurrent = this.querySelector('[data-modal-current]');
        this.counterTotal = this.querySelector('[data-modal-total]');
        this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
        this.activeIndex = 0;

        this.prevButton?.addEventListener('click', () => this.goTo(this.activeIndex - 1));
        this.nextButton?.addEventListener('click', () => this.goTo(this.activeIndex + 1));

        this.onScroll = this.onScroll.bind(this);
        this.content?.addEventListener('scroll', this.onScroll, { passive: true });
        this.content?.addEventListener('click', this.onContentClick.bind(this));
        this.content?.addEventListener('pointermove', this.onPointerMove.bind(this));

        // Document-level so arrows work wherever focus sits (e.g. on the
        // cookie banner, which renders outside the modal). Escape is handled
        // once, by ModalDialog.
        this.onDocumentKeydown = (event) => {
          if (!this.hasAttribute('open') || window.DsDrawer?.top) return;
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.goTo(this.activeIndex + 1);
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.goTo(this.activeIndex - 1);
          }
        };

        this.resizeObserver = new ResizeObserver(() => {
          if (this.hasAttribute('open')) this.goTo(this.activeIndex, { animate: false });
        });
        if (this.content) this.resizeObserver.observe(this.content);
      }

      show(opener) {
        this.syncFromGallery();
        super.show(opener);
        document.addEventListener('keydown', this.onDocumentKeydown);
        this.showActiveMedia();
      }

      hide() {
        if (!this.hasAttribute('open')) return;
        document.removeEventListener('keydown', this.onDocumentKeydown);
        this.setZoom(null);
        super.hide();
      }

      // The gallery is the single rendered copy of the variant media; the
      // viewer mirrors whichever slides it currently shows.
      syncFromGallery() {
        if (!this.content) return;
        const list = document.getElementById(`Slider-Gallery-${this.dataset.section}`);
        if (!list) return;

        const items = Array.from(list.querySelectorAll(':scope > li[data-media-id]')).filter(
          (item) => !item.classList.contains('product__media-item--variant-hidden')
        );
        const key = items.map((item) => item.dataset.mediaId).join(',');
        if (key === this.galleryKey && this.content.childElementCount) return;

        this.galleryKey = key;
        this.activeIndex = 0;
        this.content.replaceChildren(...items.map((item) => this.createSlide(item)).filter(Boolean));
      }

      createSlide(item) {
        const mediaId = (item.dataset.mediaId || '').replace(`${this.dataset.section}-`, '');
        const slide = document.createElement('div');
        slide.className = 'product-media-modal__item';
        slide.dataset.mediaId = mediaId;

        const photo = item.querySelector('.product__modal-opener--image .product__media img');
        if (photo) {
          slide.appendChild(this.createFullImage(photo, mediaId, parseInt(item.dataset.mediaWidth, 10)));
          return slide;
        }

        const deferred = item.querySelector('deferred-media, product-model');
        if (deferred) {
          const media = document.createElement(deferred.localName);
          media.className = deferred.className;
          media.dataset.mediaId = mediaId;
          const poster = deferred.querySelector('.deferred-media__poster')?.cloneNode(true);
          const template = deferred.querySelector('template')?.cloneNode(true);
          if (poster) {
            poster.id = `Deferred-Poster-Viewer-${mediaId}`;
            media.appendChild(poster);
          }
          if (template) media.appendChild(template);
          slide.appendChild(media);
          return slide;
        }

        const video = item.querySelector('video');
        if (video) {
          const clone = video.cloneNode(true);
          clone.muted = true;
          clone.loop = true;
          clone.playsInline = true;
          clone.autoplay = true;
          clone.removeAttribute('preload');
          clone.dataset.mediaId = mediaId;
          slide.appendChild(clone);
          return slide;
        }

        return null;
      }

      // Gallery files top out at the column width; the viewer asks the CDN
      // for the same photo up to its original resolution.
      createFullImage(source, mediaId, fullWidth) {
        const image = document.createElement('img');
        const base = this.baseImageUrl(source.currentSrc || source.getAttribute('src'));
        const naturalWidth = fullWidth || parseInt(source.getAttribute('width'), 10) || 2048;
        const ratio =
          (parseInt(source.getAttribute('width'), 10) || 1) / (parseInt(source.getAttribute('height'), 10) || 1);
        const widths = [550, 1100, 1445, 1680, 2048, 2200, 2890, 4096].filter((width) => width < naturalWidth);

        image.className = 'global-media-settings global-media-settings--no-shadow';
        image.srcset = [...widths.map((width) => `${this.withWidth(base, width)} ${width}w`), `${base} ${naturalWidth}w`].join(
          ', '
        );
        image.sizes = '(min-width: 750px) calc(100vw - 22rem), 100vw';
        image.src = this.withWidth(base, Math.min(1445, naturalWidth));
        image.alt = source.getAttribute('alt') || '';
        image.width = 1100;
        image.height = Math.ceil(1100 / (ratio || 1));
        image.loading = 'lazy';
        image.decoding = 'async';
        image.dataset.mediaId = mediaId;
        return image;
      }

      baseImageUrl(url) {
        if (!url) return '';
        try {
          const next = new URL(url, window.location.origin);
          ['width', 'height', 'crop'].forEach((param) => next.searchParams.delete(param));
          return `${next.origin === window.location.origin ? '' : next.origin}${next.pathname}${next.search}`;
        } catch (error) {
          return url;
        }
      }

      withWidth(url, width) {
        return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
      }

      getSlides() {
        if (!this.content) return [];
        return Array.from(this.content.children).filter(
          (slide) =>
            !slide.classList.contains('product__media-item--variant-hidden') &&
            getComputedStyle(slide).display !== 'none'
        );
      }

      showActiveMedia() {
        const slides = this.getSlides();
        const mediaId = this.openedBy?.getAttribute('data-media-id');
        let index = mediaId
          ? slides.findIndex(
              (slide) =>
                slide.getAttribute('data-media-id') === mediaId ||
                slide.querySelector(`[data-media-id="${mediaId}"]`)
            )
          : 0;
        if (index < 0) index = 0;

        this.renderThumbs(slides);
        this.goTo(index, { animate: false });

        const activeMedia = slides[index]?.querySelector('deferred-media') || slides[index];
        if (
          activeMedia?.nodeName === 'DEFERRED-MEDIA' &&
          activeMedia.querySelector('template')?.content.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }
      }

      goTo(index, { animate = true } = {}) {
        const slides = this.getSlides();
        if (!slides.length || !this.content) return;

        const nextIndex = Math.max(0, Math.min(index, slides.length - 1));
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.content.scrollTo({
          left: nextIndex * this.content.clientWidth,
          behavior: animate && !reduceMotion ? 'smooth' : 'auto',
        });
        this.setActive(nextIndex, slides);
      }

      onScroll() {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          if (!this.content?.clientWidth) return;
          const index = Math.round(this.content.scrollLeft / this.content.clientWidth);
          if (index !== this.activeIndex) this.setActive(index);
        });
      }

      setActive(index, slides = this.getSlides()) {
        const changed = index !== this.activeIndex;
        this.activeIndex = index;
        const total = slides.length;

        if (changed) {
          this.setZoom(null);
          window.pauseAllMedia?.();
        }

        [index - 1, index, index + 1].forEach((i) => {
          slides[i]?.querySelectorAll('img[loading="lazy"]').forEach((image) => {
            image.loading = 'eager';
          });
          if (slides[i]?.matches('img[loading="lazy"]')) slides[i].loading = 'eager';
        });

        const multiple = total > 1;
        if (this.counter) this.counter.hidden = !multiple;
        if (this.counterCurrent) this.counterCurrent.textContent = String(index + 1);
        if (this.counterTotal) this.counterTotal.textContent = String(total);

        if (this.prevButton) {
          this.prevButton.hidden = !multiple;
          this.prevButton.disabled = index <= 0;
        }
        if (this.nextButton) {
          this.nextButton.hidden = !multiple;
          this.nextButton.disabled = index >= total - 1;
        }

        if (!this.thumbs) return;
        Array.from(this.thumbs.children).forEach((thumb, i) => {
          if (i === index) thumb.setAttribute('aria-current', 'true');
          else thumb.removeAttribute('aria-current');
        });

        const current = this.thumbs.children[index];
        if (current && this.thumbs.scrollWidth > this.thumbs.clientWidth) {
          const left = current.offsetLeft - (this.thumbs.clientWidth - current.offsetWidth) / 2;
          this.thumbs.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
        }
      }

      renderThumbs(slides) {
        if (!this.thumbs) return;

        this.thumbs.hidden = slides.length < 2;
        this.thumbs.replaceChildren(...slides.map((slide, index) => this.createThumb(slide, index)));
      }

      createThumb(slide, index) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'product-media-modal__thumb';
        button.setAttribute('aria-label', `View image ${index + 1}`);

        const source = slide.matches('img') ? slide : slide.querySelector('img');
        const src = this.thumbSrc(source?.getAttribute('src'));

        if (src) {
          const image = document.createElement('img');
          image.alt = '';
          image.src = src;
          image.loading = 'lazy';
          image.decoding = 'async';
          button.appendChild(image);
        } else {
          button.classList.add('product-media-modal__thumb--video');
          button.textContent = '360°';
        }

        button.addEventListener('click', () => this.goTo(index));
        return button;
      }

      thumbSrc(url) {
        if (!url) return url;
        try {
          const next = new URL(url, window.location.origin);
          next.searchParams.set('width', '200');
          next.searchParams.delete('height');
          next.searchParams.delete('crop');
          return `${next.pathname}${next.search}`;
        } catch (error) {
          return url;
        }
      }

      // Clicking the photo zooms it (desktop); clicking the empty stage
      // around it closes the viewer.
      onContentClick(event) {
        const image = event.target.closest('img');
        const slide = this.getSlides()[this.activeIndex];

        if (image && slide?.contains(image) && !image.closest('deferred-media, product-model')) {
          if (!this.finePointer.matches) return;
          this.setZoom(this.zoomedSlide === slide ? null : slide, event);
          return;
        }

        if (event.target.closest('deferred-media, product-model, button, a')) return;
        this.hide();
      }

      setZoom(slide, event) {
        if (this.zoomedSlide) {
          this.zoomedSlide.classList.remove('is-zoomed');
          this.zoomedSlide.querySelector('img')?.style.removeProperty('transform-origin');
          if (this.zoomedSlide.matches('img')) this.zoomedSlide.style.removeProperty('transform-origin');
        }

        this.zoomedSlide = slide || null;
        this.classList.toggle('is-zoomed', !!slide);
        if (!slide) return;

        slide.classList.add('is-zoomed');
        if (event) this.onPointerMove(event);
      }

      onPointerMove(event) {
        if (!this.zoomedSlide) return;
        const image = this.zoomedSlide.matches('img') ? this.zoomedSlide : this.zoomedSlide.querySelector('img');
        if (!image) return;

        const rect = this.zoomedSlide.getBoundingClientRect();
        const x = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100));
        const y = Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100));
        image.style.transformOrigin = `${x}% ${y}%`;
      }
    }
  );
}
